import { calculateOrderPromotionDiscount } from "../features/promotions/helpers/utils";
import { store } from "../state/store";
import {
  FIXED_SURCHARGE,
  ORDER_TYPE_DELIVERY,
} from "./constants";

const calculateSubTotal = (item: any) => {
  let price = item.unit_price * (item.count || 0);
  let subTotal = 0;
  let modifierSubtotal = 0;
  if (item.groups.length) {
    item.groups.map((group: any) => {
      modifierSubtotal += group.items.reduce((amount: number, item: any) => {
        if (item.selected) {
          if (item.count) {
            if (item.count > 0) {
              return amount + item.final_price * item.count;
            }
          } else {
            return amount + item.final_price;
          }
        }
        return amount;
      }, 0);
    });
    subTotal = price + modifierSubtotal * (item.count || 0);
  } else {
    subTotal = price;
  }

  return subTotal;
};

const findItemTotalAndTax = (
  itemPrice: number,
  itemTaxes: any,
  taxes: any,
  taxInclusive: boolean
) => {
  let itemTotal = itemPrice;
  let taxTotal = 0;
  if (itemTaxes.length) {
    itemTaxes.map((itemTax: any) => {
      itemTax.taxes.map((tax: any) => {
        let taxRate = findTaxRate(tax, taxes);
        if (taxInclusive) {
          taxTotal += itemPrice - (itemPrice / (1 + taxRate));
        } else {
          itemTotal += itemPrice * taxRate;
          taxTotal += itemPrice * taxRate;
        }
      });
    });
  }
  return {
    itemTotal,
    taxTotal
  };
};

const findTaxRate = (taxId: any, taxes: any) => {
  let taxRate = 0;
  if (taxes.length) {
    let tax = taxes.find((tax: any) => tax.id === taxId);
    if (tax && tax.type === "PERCENTAGE") {
      taxRate = tax.value / 100;
    }
  }
  return taxRate;
};

const calculateSubTotalAndTotal = (items: any, menu: any) => {
  let total = 0;
  let subTotal = 0;
  let tax = 0;
  items.forEach((item: any) => {
    subTotal += parseFloat(item.sub_total);
    let totalAndTaxObj: any = findItemTotalAndTax(
      parseFloat(item.sub_total), item.taxes, menu.taxes, menu.is_tax_inclusive
    )
    total += parseFloat(totalAndTaxObj.itemTotal);
    tax += parseFloat(totalAndTaxObj.taxTotal);
  });

  return {
    subTotal,
    total,
    tax,
  };
}

const calculateSurcharges = (
  surcharges: any,
  cartSubTotal: any,
  taxInclusive: boolean,
  orderType: string,
  taxes: any,
  deliveryCharge: any,
  deliveryChargeId: any,
) => {
  let subTotal = 0;
  let total = 0;
  let tax = 0;
  surcharges = surcharges.filter((charge: any) => {
    if (orderType == ORDER_TYPE_DELIVERY) {
      return charge.applicable_on_weborder;
    } else {
      return charge.applicable_on_takeaway && charge.id != deliveryChargeId;
    }
  });
  let breakDown = surcharges.map((charge: any) => {
    let chargeAmount: number, chargeValue: number;
    if (charge.id == deliveryChargeId) {
      chargeValue = deliveryCharge == null ? charge.value : deliveryCharge;
    } else {
      chargeValue = charge.value;
    }
    if (charge.type === FIXED_SURCHARGE) {
      chargeAmount = chargeValue;
    } else {
      chargeAmount = cartSubTotal * (chargeValue / 100);
    }
    subTotal += chargeAmount;

    let totalAndTaxObj: any = findItemTotalAndTax(
      chargeAmount, charge.taxes, taxes, taxInclusive
    )
    total += parseFloat(totalAndTaxObj.itemTotal);
    tax += parseFloat(totalAndTaxObj.taxTotal);

    return {
      id: charge.id,//for promotion matching purposes
      name: charge.name,
      amount: chargeAmount,
      type: charge.type,
    };
  });

  return {
    subTotal,
    tax,
    total,
    breakDown,
  };
}

/**
 * Find the effective tax rate for a given tax ID.
 * If the tax is a group, it calculates the sum of child tax rates.
 * @param taxId - The ID of the tax to look up
 * @param allTaxes - All available taxes
 * @returns The effective tax rate as a decimal (e.g., 0.05 for 5%)
 */
const getEffectiveTaxRate = (taxId: any, allTaxes: any[]): number => {
  const tax = allTaxes.find((t: any) => t.id === taxId);
  if (!tax) return 0;

  if (tax.is_group && tax.child_tax_rate_ids) {
    // Sum up child tax rates for group taxes
    return tax.child_tax_rate_ids.reduce((sum: number, childId: any) => {
      const childTax = allTaxes.find((t: any) => t.id === childId);
      if (childTax && childTax.type === "PERCENTAGE") {
        return sum + childTax.value / 100;
      }
      return sum;
    }, 0);
  }

  if (tax.type === "PERCENTAGE") {
    return tax.value / 100;
  }
  return 0;
};

/**
 * Calculate per-tax breakdown from items and surcharges.
 * Handles group taxes by expanding them into child taxes,
 * aggregates same taxes from different groups, and includes non-group taxes.
 * @param items - Cart items with their taxes
 * @param surcharges - Applicable surcharges with their taxes  
 * @param allTaxes - All available taxes from menu
 * @param taxInclusive - Whether prices are tax inclusive
 * @param cartSubTotal - The cart subtotal for calculating surcharge amounts
 * @param orderType - Order type to filter applicable surcharges
 * @param deliveryChargeId - Delivery charge ID to exclude for non-delivery orders
 * @returns Map of tax ID to { id, name, amount, rate, type }
 */
const calculatePerTaxBreakdown = (
  items: any[],
  surcharges: any[],
  allTaxes: any[],
  taxInclusive: boolean,
  cartSubTotal: number,
  orderType: string,
  deliveryChargeId: any
): Map<any, any> => {
  const taxBreakdownMap = new Map<any, any>();

  // Helper to add tax amount to the breakdown map
  const addToBreakdown = (taxId: any, taxableAmount: number) => {
    const tax = allTaxes.find((t: any) => t.id === taxId);
    if (!tax) return;

    // If it's a group tax, expand into child taxes
    if (tax.is_group && tax.child_tax_rate_ids) {
      const isCompound = tax.is_compound === true;
      const compoundSeqNos: any[] = tax.compound_sequence_nos || [];

      const childTaxRates = allTaxes
        .filter((t: any) => tax.child_tax_rate_ids.includes(t.id))
        .map((t: any) => ({
          ...t,
          _seqNo: compoundSeqNos[tax.child_tax_rate_ids.indexOf(t.id)] ?? null,
        }));

      // For compound groups, sort children by compound_sequence_no ASC (nulls last)
      // so that we cascade in the correct order (e.g., VAT seq=1, SES seq=2).
      if (isCompound) {
        childTaxRates.sort((a: any, b: any) => {
          if (a._seqNo === null && b._seqNo === null) return 0;
          if (a._seqNo === null) return 1;
          if (b._seqNo === null) return -1;
          return a._seqNo - b._seqNo;
        });
      }

      // For tax-inclusive compound groups, back-calculate the pre-tax base using
      // the group's effective compound rate (stored in tax.value by MRMS).
      const effectiveGroupRate = tax.value / 100;
      const preTaxBase = (taxInclusive && effectiveGroupRate > 0)
        ? taxableAmount / (1 + effectiveGroupRate)
        : taxableAmount;

      // runningBase cascades for compound groups; stays fixed for parallel groups.
      let runningBase = preTaxBase;

      childTaxRates.forEach((childTax: any) => {
        if (childTax.type === "PERCENTAGE") {
          const taxRate = childTax.value / 100;
          let taxAmount = 0;

          if (taxInclusive) {
            // For tax-inclusive compound: cascade off the back-calculated pre-tax base.
            // For tax-inclusive parallel: proportional split of total included tax.
            if (isCompound) {
              taxAmount = runningBase * taxRate;
              runningBase += taxAmount;
            } else {
              if (effectiveGroupRate > 0) {
                const proportion = taxRate / effectiveGroupRate;
                const totalTaxInAmount = taxableAmount - preTaxBase;
                taxAmount = totalTaxInAmount * proportion;
              }
            }
          } else {
            // Tax-exclusive: cascade running base for compound, fixed base for parallel.
            taxAmount = runningBase * taxRate;
            if (isCompound) runningBase += taxAmount;
          }

          if (taxBreakdownMap.has(childTax.id)) {
            const existing = taxBreakdownMap.get(childTax.id);
            existing.amount += taxAmount;
          } else {
            taxBreakdownMap.set(childTax.id, {
              id: childTax.id,
              name: childTax.name,
              amount: taxAmount,
              rate: childTax.value,
              type: childTax.type,
              is_compound: isCompound,
              compound_sequence_no: isCompound ? childTax._seqNo : null,
            });
          }
        }
      });
    } else if (tax.type === "PERCENTAGE") {
      // Non-group tax
      const taxRate = tax.value / 100;
      let taxAmount = 0;

      if (taxInclusive) {
        taxAmount = taxableAmount - taxableAmount / (1 + taxRate);
      } else {
        taxAmount = taxableAmount * taxRate;
      }

      if (taxBreakdownMap.has(tax.id)) {
        const existing = taxBreakdownMap.get(tax.id);
        existing.amount += taxAmount;
      } else {
        taxBreakdownMap.set(tax.id, {
          id: tax.id,
          name: tax.name,
          amount: taxAmount,
          rate: tax.value,
          type: tax.type,
          is_compound: false,
          compound_sequence_no: null,
        });
      }
    }
  };

  // Process item taxes
  items.forEach((item: any) => {
    if (item.taxes && item.taxes.length) {
      const itemSubTotal = parseFloat(item.sub_total) || 0;
      item.taxes.forEach((itemTax: any) => {
        if (itemTax.taxes && itemTax.taxes.length) {
          itemTax.taxes.forEach((taxId: any) => {
            addToBreakdown(taxId, itemSubTotal);
          });
        }
      });
    }
  });

  // Process surcharge taxes (only applicable ones)
  const applicableSurcharges = surcharges.filter((charge: any) => {
    if (orderType == ORDER_TYPE_DELIVERY) {
      return charge.applicable_on_weborder;
    } else {
      return charge.applicable_on_takeaway && charge.id != deliveryChargeId;
    }
  });

  applicableSurcharges.forEach((charge: any) => {
    let chargeAmount: number;
    if (charge.type === "FIXED") {
      chargeAmount = charge.value;
    } else {
      chargeAmount = cartSubTotal * (charge.value / 100);
    }

    if (charge.taxes && charge.taxes.length) {
      charge.taxes.forEach((chargeTax: any) => {
        if (chargeTax.taxes && chargeTax.taxes.length) {
          chargeTax.taxes.forEach((taxId: any) => {
            addToBreakdown(taxId, chargeAmount);
          });
        }
      });
    }
  });

  return taxBreakdownMap;
};

/**
 * Calculate the tax breakdown for all taxes in the cart.
 * Shows breakdown of all taxes, combines same taxes from different groups,
 * and includes non-group taxes.
 * @param taxes - All available taxes from menu
 * @param totalTaxAmount - Total tax amount (used as fallback)
 * @param items - Cart items (optional, for detailed breakdown)
 * @param surcharges - Surcharges (optional, for detailed breakdown)
 * @param taxInclusive - Whether prices are tax inclusive
 * @param cartSubTotal - Cart subtotal for surcharge calculation
 * @param orderType - Order type to filter applicable surcharges
 * @param deliveryChargeId - Delivery charge ID to exclude for non-delivery orders
 * @returns array of taxes with calculated tax amounts
 */
const calculateTaxBreakdown = (
  taxes: any,
  totalTaxAmount: number,
  items: any[] = [],
  surcharges: any[] = [],
  taxInclusive: boolean = false,
  cartSubTotal: number = 0,
  orderType: string = '',
  deliveryChargeId: any = null
) => {
  // If total tax is 0 or no taxes, return empty breakdown
  if (totalTaxAmount <= 0 || !taxes || taxes.length === 0) {
    return [];
  }

  // If tax breakdown is not enabled on any tax, return empty breakdown
  // The taxBreakdownEnabled flag is added to all taxes via addTaxBreakdownKeyToTaxes
  const anyTaxHasBreakdownDisabled = taxes.some(
    (tax: any) => tax.taxBreakdownEnabled === false
  );
  if (anyTaxHasBreakdownDisabled) {
    return [];
  }

  // Calculate detailed per-tax breakdown from items and surcharges
  if (items.length > 0) {
    const taxBreakdownMap = calculatePerTaxBreakdown(
      items,
      surcharges,
      taxes,
      taxInclusive,
      cartSubTotal,
      orderType,
      deliveryChargeId
    );

    // Convert map to array
    const breakdown = Array.from(taxBreakdownMap.values());

    // Return the breakdown if we calculated any taxes
    if (breakdown.length > 0) {
      return breakdown;
    }
  }

  // If no items or couldn't calculate breakdown, return empty
  return [];
};

const calculateAmountsAndBreakdowns = (
  items: any,
  menu: any,
  orderType: string,
  discount: any = null,
  loyalty: any = null,
  area: any = {},
  deliveryChargeId: any = null,
  tips: number | null = null,
  orderPromotions: any[] = []
) => {
  // Finding Subtotal, Total and Tax of Items in Cart
  let totalObject = calculateSubTotalAndTotal(items, menu);
  let itemSubTotal = totalObject.subTotal;
  let itemTaxTotal = totalObject.tax;

  // Finding Subtotal, Total and Tax of Surcharges
  let surchargeObject = calculateSurcharges(
    menu.surcharges, itemSubTotal, menu.is_tax_inclusive, orderType,
    menu.taxes, area.delivery_charge, deliveryChargeId
  );
  let surchargeSubTotal = surchargeObject.subTotal;
  let surchargeTaxTotal = surchargeObject.tax;
  let taxTotal = itemTaxTotal + surchargeTaxTotal;

  let totalAmount;
  let discountTotal = 0;
  let discountedAmount = 0;
  let discountedTax = 0;
  // Initialize with actual item values - these will be overridden if order-level discounts/promotions apply
  let subTotalAfterDiscount = itemSubTotal;
  let subTotalTaxAfterDiscount = itemTaxTotal;
  let orderPromotionDiscount = 0;

  // Finding discounted item price and tax
  if ( discount ) {
    discountedAmount = discount.discountedAmount;
    let discountPercentage = parseFloat(discountedAmount) /
      parseFloat(discount.maxdiscountableAmount);
    discountPercentage = discountPercentage || 0;
    if ( discount.on_total ) {
      discountedTax = taxTotal * discountPercentage;
    } else {
      discountedTax = itemTaxTotal * discountPercentage;
    }
    if (menu.is_tax_inclusive) {
      discountTotal = discountedAmount;
    } else {
      discountTotal = discountedAmount + discountedTax;
    }

    subTotalAfterDiscount = itemSubTotal - itemSubTotal * discountPercentage;
    subTotalTaxAfterDiscount = itemTaxTotal - itemTaxTotal * discountPercentage;
  }
  taxTotal = taxTotal - discountedTax;
  if (menu.is_tax_inclusive) {
    totalAmount = parseFloat(formatDecimal(itemSubTotal)) +
      parseFloat(formatDecimal(surchargeSubTotal)) -
      parseFloat(formatDecimal(discountedAmount));
  } else {
    totalAmount = parseFloat(formatDecimal(itemSubTotal)) +
      parseFloat(formatDecimal(surchargeSubTotal)) +
      parseFloat(formatDecimal(taxTotal)) -
      parseFloat(formatDecimal(discountedAmount));
  }
  let totalBeforeDiscount = totalAmount +
    parseFloat(formatDecimal(discountedAmount)) +
    parseFloat(formatDecimal(discountedTax));

  let totalBeforeLoyalty = totalAmount;
  let loyaltyTax = 0;
  let loyaltyAsDiscount = store.getState().loyaltyConfig?.is_discount;
  if (loyaltyAsDiscount) {
    totalBeforeLoyalty =
      parseFloat(formatDecimal(itemSubTotal)) +
      parseFloat(formatDecimal(surchargeSubTotal)) -
      parseFloat(formatDecimal(discountedAmount));
  }
  if ( loyalty ) {
    if (loyaltyAsDiscount) {
      let loyaltyPercentage = parseFloat(loyalty) / parseFloat(totalBeforeLoyalty);
      loyaltyTax = taxTotal * loyaltyPercentage;
    }

    taxTotal -= loyaltyTax;
    if (menu.is_tax_inclusive) {
      totalAmount =
        parseFloat(formatDecimal(itemSubTotal)) +
        parseFloat(formatDecimal(surchargeSubTotal)) -
        parseFloat(formatDecimal(discountedAmount)) -
        parseFloat(formatDecimal(loyalty));
    } else {
      totalAmount =
        parseFloat(formatDecimal(itemSubTotal)) +
        parseFloat(formatDecimal(surchargeSubTotal)) +
        parseFloat(formatDecimal(taxTotal)) -
        parseFloat(formatDecimal(discountedAmount)) -
        parseFloat(formatDecimal(loyalty));
    }
  }
  if (orderPromotions.length) {
    const highestPriotityPromotion = orderPromotions[0];
    const { discountAmount, taxDiscount, discountRatio } = calculateOrderPromotionDiscount(
      highestPriotityPromotion,
      itemSubTotal,
      surchargeSubTotal,
      surchargeObject.breakDown,
      taxTotal,
    );
    orderPromotionDiscount = discountAmount;
    totalAmount -= orderPromotionDiscount;
    subTotalAfterDiscount = itemSubTotal - itemSubTotal * discountRatio;
    subTotalTaxAfterDiscount = itemTaxTotal - itemTaxTotal * discountRatio;
    if (taxTotal && !menu.is_tax_inclusive) {
      const totalWithoutTax = totalAmount - taxTotal;
      taxTotal = taxTotal - taxDiscount;
      totalAmount = totalWithoutTax + taxTotal;
    }
  }

  let totalBeforeTip = totalAmount;
  if (Number(tips) > 0) {
    totalAmount += parseFloat(formatDecimal(tips));
  }
  let taxBreakdown = calculateTaxBreakdown(
    menu.taxes,
    taxTotal,
    items,
    menu.surcharges || [],
    menu.is_tax_inclusive,
    itemSubTotal,
    orderType,
    deliveryChargeId
  );

  return {
    subTotal: formatDecimal(itemSubTotal),
    total: formatDecimal(totalAmount),
    surchargeSubTotal: formatDecimal(surchargeSubTotal),
    surchargeTotal: formatDecimal(surchargeObject.total),
    discountTotal: formatDecimal(discountTotal),
    loyaltyTotal: formatDecimal(loyalty),
    taxTotal: formatDecimal(taxTotal),
    taxBreakdown: taxBreakdown,
    surchargeBreakDown: surchargeObject.breakDown,
    totalBeforeDiscount: formatDecimal(totalBeforeDiscount),
    totalBeforeLoyalty: formatDecimal(totalBeforeLoyalty),
    subTotalAfterDiscount: formatDecimal(subTotalAfterDiscount),
    subTotalTaxAfterDiscount: formatDecimal(subTotalTaxAfterDiscount),
    tips: formatDecimal(tips),
    orderPromotionDiscount: formatDecimal(orderPromotionDiscount),
    totalBeforeTip: formatDecimal(totalBeforeTip),
  };
};

const formatDecimal = (number: any, precision = localStorage.precision) => {
  if (!precision) {
    precision = 2;
  }

  number = parseFloat(parseFloat(number).toFixed(7));

  let rounder = Math.pow(10, precision);

  return (Math.round(number * rounder) / rounder).toFixed(precision);
}

export {
  calculateSubTotal,
  calculateAmountsAndBreakdowns,
  formatDecimal
};
